import { useMemo } from 'react';

import { milestoneDateDisplay, milestoneHasValue } from 'helpers/formatDateOnly';
import { getInitialsFromName } from 'helpers/getInitialsFromName';
import { getTierPropsFromMilestones } from 'helpers/getTierPropsFromMilestones';

import { GlassCard } from '../../GlassCard/GlassCard';
import type { AdvisorDashboardMilestones, GlassCardSpan, GlassCardVariant } from '../../types';

import './_programStatusCard.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import i18n from 'i18n-react';
import { Link } from 'react-router-dom';
export interface ProgramStatusCardProps {
  span?: GlassCardSpan;
  variant?: GlassCardVariant;
  /** Forwarded to `GlassCard` when the middle stack uses extra content inset (e.g. carousel chevrons). */
  addContentGutter?: boolean;
  /** When true ( `metallic_tiers` flag), show current tier row and apply tier colors to the header star. */
  metallicTiers?: boolean;
  lightTheme?: boolean;
  advisorMilestones?: AdvisorDashboardMilestones | null;
  profilePictureUrl?: string | null;
  firstName: string;
  lastName: string;
  isLoggedIn: boolean;
  profileFrameUrl: string;
  /** External "Your Rep" URL to use instead of `/main/contact` (from `alternative_contact_us`/`contact_us_url`). */
  contactUrl?: string | null;
}

export function ProgramStatusCard({
  span,
  variant,
  addContentGutter,
  metallicTiers = false,
  lightTheme = false,
  advisorMilestones = null,
  profilePictureUrl = null,
  firstName,
  lastName,
  isLoggedIn,
  profileFrameUrl,
  contactUrl = null,
}: ProgramStatusCardProps) {
  const initials = useMemo(() => getInitialsFromName(firstName, lastName), [firstName, lastName]);

  const hasRegisteredOn = milestoneHasValue(advisorMilestones?.joined_on_display, advisorMilestones?.joined_on);
  const hasGraduatedOn = milestoneHasValue(advisorMilestones?.graduated_on_display, advisorMilestones?.graduated_on);
  const hasBadgesEarned = advisorMilestones !== null && advisorMilestones.total_badges_earned > 0;

  const joinedStatDisplay = milestoneDateDisplay(advisorMilestones?.joined_on_display, advisorMilestones?.joined_on);
  const graduatedStatDisplay = milestoneDateDisplay(
    advisorMilestones?.graduated_on_display,
    advisorMilestones?.graduated_on,
  );

  const {
    slug: tierSlugNorm,
    display: tierValueDisplay,
    showRow: showTierRow,
  } = getTierPropsFromMilestones(advisorMilestones, metallicTiers);

  const badgeCountStat = advisorMilestones !== null ? advisorMilestones.total_badges_earned : 0;

  const hasAnyStatsContent =
    hasRegisteredOn || hasGraduatedOn || hasBadgesEarned || (showTierRow && tierValueDisplay !== '');

  const headerAvatar = useMemo(() => {
    if (profilePictureUrl) {
      return (
        <div className="program-status-card__header-avatar">
          <Link to="/main/profile-image" className="program-status-card__avatar program-status-card__avatar--in-header">
            <Tooltip
              title={i18n.translate('glassWidgets.programStatus.changeProfilePicture') as string}
              slotProps={{ tooltip: { sx: { fontSize: '1.6rem' } } }}
            >
              <img
                src={profilePictureUrl}
                alt={initials !== null && initials !== '' ? `profile-image-${initials}` : 'Profile'}
              />
            </Tooltip>
          </Link>
        </div>
      );
    }
    if (initials) {
      return (
        <div className="program-status-card__header-avatar">
          <Link
            to="/main/profile-image"
            className="program-status-card__avatar program-status-card__avatar--initials program-status-card__avatar--in-header"
          >
            <Tooltip
              title={i18n.translate('glassWidgets.programStatus.changeProfilePicture') as string}
              slotProps={{ tooltip: { sx: { fontSize: '1.6rem' } } }}
            >
              <span className="program-status-card__avatar-initials" aria-hidden="true">
                {initials}
              </span>
            </Tooltip>
          </Link>
        </div>
      );
    }
    return null;
  }, [profilePictureUrl, initials]);

  const hasProfileFrameUrl = profileFrameUrl.trim() !== '';

  const actionButtons = useMemo(
    () => (
      <div className="program-status-card__actions">
        {isLoggedIn && contactUrl && (
          <a href={contactUrl} target="_blank" rel="noreferrer noopener" className="program-status-card__action-btn">
            <FontAwesomeIcon icon={['fas', 'comment-dots']} />
            <span>{i18n.translate('glassWidgets.programStatus.yourRep') as string}</span>
          </a>
        )}
        {isLoggedIn && !contactUrl && (
          <Link to="/main/contact" className="program-status-card__action-btn">
            <FontAwesomeIcon icon={['fas', 'comment-dots']} />
            <span>{i18n.translate('glassWidgets.programStatus.yourRep') as string}</span>
          </Link>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'comment-dots']} />
            <span>{i18n.translate('glassWidgets.programStatus.yourRep') as string}</span>
          </span>
        )}

        {isLoggedIn && (
          <button
            type="button"
            className="program-status-card__action-btn settings"
            data-trigger="settings"
            data-fancybox-rel="settings"
            disabled={!hasProfileFrameUrl}
          >
            <FontAwesomeIcon icon={['fas', 'user']} />
            <span>{i18n.translate('glassWidgets.programStatus.myProfile') as string}</span>
          </button>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'user']} />
            <span>{i18n.translate('glassWidgets.programStatus.myProfile') as string}</span>
          </span>
        )}

        {isLoggedIn && (
          <Link to="/main/favorites" className="program-status-card__action-btn">
            <FontAwesomeIcon icon={['fas', 'heart']} />
            <span>{i18n.translate('glassWidgets.programStatus.favorites') as string}</span>
          </Link>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'heart']} />
            <span>{i18n.translate('glassWidgets.programStatus.favorites') as string}</span>
          </span>
        )}

        {isLoggedIn && (
          <Link to="/logout" className="program-status-card__action-btn program-status-card__action-btn--logout">
            <FontAwesomeIcon icon={['fas', 'sign-out-alt']} />
            <span>{i18n.translate('general.logOut')}</span>
          </Link>
        )}

        {!isLoggedIn && (
          <Link to="/login" className="program-status-card__action-btn">
            <FontAwesomeIcon icon={['fas', 'sign-in-alt']} />
            <span>{i18n.translate('general.logIn')}</span>
          </Link>
        )}
      </div>
    ),
    [isLoggedIn, hasProfileFrameUrl, contactUrl],
  );

  return (
    <GlassCard
      icon={['fas', 'star']}
      title={i18n.translate('glassWidgets.programStatus.title') as string}
      span={span}
      variant={variant}
      addContentGutter={addContentGutter}
      lightTheme={lightTheme}
      className={clsx('program-status-card', {
        'program-status-card--tier-default': !metallicTiers || !tierSlugNorm,
        'program-status-card--tier-bronze': metallicTiers && tierSlugNorm === 'bronze',
        'program-status-card--tier-silver': metallicTiers && tierSlugNorm === 'silver',
        'program-status-card--tier-gold': metallicTiers && tierSlugNorm === 'gold',
        'program-status-card--tier-platinum': metallicTiers && tierSlugNorm === 'platinum',
      })}
      headerExtra={headerAvatar ?? undefined}
      bottomActions={actionButtons}
    >
      {hasAnyStatsContent && (
        <ul className="program-status-card__stats">
          {hasRegisteredOn && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon icon={['fas', 'star']} className="program-status-card__stat-icon" />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.registeredOn') as string}
              </span>
              <span className="program-status-card__stat-value">{joinedStatDisplay}</span>
            </li>
          )}
          {hasGraduatedOn && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon icon={['fas', 'file-certificate']} className="program-status-card__stat-icon" />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.graduatedOn') as string}
              </span>
              <span className="program-status-card__stat-value">{graduatedStatDisplay}</span>
            </li>
          )}
          {hasBadgesEarned && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon icon={['fas', 'trophy']} className="program-status-card__stat-icon" />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.totalBadgesEarned') as string}
              </span>
              <span className="program-status-card__stat-value">{badgeCountStat}</span>
            </li>
          )}

          {showTierRow && tierValueDisplay !== '' && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon icon={['fas', 'award']} className="program-status-card__stat-icon" />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.currentTier') as string}
              </span>
              <span
                className={clsx('program-status-card__stat-value program-status-card__tier-value', {
                  'program-status-card__tier-value--bronze': tierSlugNorm === 'bronze',
                  'program-status-card__tier-value--silver': tierSlugNorm === 'silver',
                  'program-status-card__tier-value--gold': tierSlugNorm === 'gold',
                  'program-status-card__tier-value--platinum': tierSlugNorm === 'platinum',
                })}
              >
                <FontAwesomeIcon icon={['fas', 'award']} />
                {tierValueDisplay}
              </span>
            </li>
          )}
        </ul>
      )}

      {profileFrameUrl && (
        <div
          data-fancybox="settings"
          data-type="iframe"
          className="settings"
          // @ts-expect-error — fancybox reads href from DOM attribute
          href={profileFrameUrl}
          style={{ display: 'none' }}
        />
      )}
    </GlassCard>
  );
}
